import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth, storage } from '../firebaseConfig';
import { doc, getDoc, collection, addDoc, getDocs, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import Toast from './Toast';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';

interface Document {
    id: string;
    name: string;
    documentName?: string;
    uploadTime: Date;
    status: 'requested' | 'uploaded';
    downloadUrl?: string;
    category?: string;
}

interface DocumentCategory {
    id: string;
    categoryName: string;
    documentValues: string[];
}

interface RequestDocumentProps {
    write: boolean;
}

const RequestDocument = ({ write }: RequestDocumentProps) => {

    const { currentSubAgent } = useSubAgentAuth();

    const { leadId } = useParams();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [studentName, setStudentName] = useState<string>("");
    const [companyName, setCompanyName] = useState("");
    const [companyId, setCompanyId] = useState("");
    const [studentId, setStudentId] = useState<string>("");
    const [enteredDocumentName, setEnteredDocumentName] = useState<string>("");
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState<boolean>(false);
    const [isDocumentDropdownOpen, setIsDocumentDropdownOpen] = useState<boolean>(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [documentCategories, setDocumentCategories] = useState<DocumentCategory[]>([]);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const documentTypes = [
        "Transcript",
        "Degree Certificate",
        "Recommendation Letter",
    ];

    useEffect(() => {
        fetchData();
    }, [leadId]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }
            if (!companyId) return;
            setCompanyId(companyId);

            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            if (companyDoc.exists()) {
                setCompanyName(companyDoc.data().name || "Unknown Company");
            }

            // Fetch document categories
            const categoriesRef = collection(db, `companies/${companyId}/leadDocumentCategories`);
            const categoriesSnapshot = await getDocs(categoriesRef);
            const categoriesData: DocumentCategory[] = categoriesSnapshot.docs.map(doc => ({
                id: doc.id,
                categoryName: doc.data().categoryName,
                documentValues: doc.data().documentValues || []
            }));
            setDocumentCategories(categoriesData);

            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const studentData = leadDoc.data();
                setStudentName(studentData.name || "Unknown Student");
                setStudentId(leadDoc.id || "");
            }

            // Fetch documents
            const documentsRef = collection(db, `companies/${companyId}/leads/${leadId}/documents`);
            const documentsQuery = query(documentsRef, orderBy('uploadTime', 'desc'));
            const documentsSnapshot = await getDocs(documentsQuery);

            const documentsData: Document[] = [];
            for (const doc of documentsSnapshot.docs) {
                const data = doc.data();
                if (data.documentName) {
                    // This is an uploaded document, get the download URL
                    const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${data.documentName}`);
                    const downloadUrl = await getDownloadURL(storageRef);
                    documentsData.push({
                        id: doc.id,
                        name: data.name,
                        documentName: data.documentName,
                        uploadTime: data.uploadTime.toDate(),
                        status: 'uploaded',
                        downloadUrl,
                        category: data.category || undefined
                    });
                } else {
                    documentsData.push({
                        id: doc.id,
                        name: data.name,
                        uploadTime: data.uploadTime.toDate(),
                        status: 'requested',
                        category: data.category || undefined
                    });
                }
            }
            setDocuments(documentsData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getDocumentName = () => {
        return enteredDocumentName.trim() || selectedDocument;
    };

    const getSelectedCategory = () => {
        return selectedCategory;
    };

    const groupDocumentsByCategory = () => {
        const categorized: { [key: string]: Document[] } = {};
        const uncategorized: Document[] = [];

        documents.forEach(doc => {
            if (doc.category) {
                if (!categorized[doc.category]) {
                    categorized[doc.category] = [];
                }
                categorized[doc.category].push(doc);
            } else {
                uncategorized.push(doc);
            }
        });

        return { categorized, uncategorized };
    };

    const handleRequestDocument = async () => {
        try {
            setIsLoading(true);
            const documentName = getDocumentName();
            const category = getSelectedCategory();
            if (!documentName || !companyId || !leadId) return;

            const docData: any = {
                name: documentName,
                uploadTime: new Date(),
            };

            if (category) {
                docData.category = category;
            }

            const docRef = await addDoc(collection(db, `companies/${companyId}/leads/${leadId}/documents`), docData);

            await fetchData(); // Refresh the documents list
            setSelectedDocument("");
            setSelectedCategory("");
            setEnteredDocumentName("");
        } catch (error) {
            console.error("Error requesting document:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getAvailableDocuments = () => {
        if (!selectedCategory) return [];
        const category = documentCategories.find(cat => cat.categoryName === selectedCategory);
        return category ? category.documentValues : [];
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setIsLoading(true);
            const file = event.target.files?.[0];
            if (!file || !companyId || !leadId) return;

            // Check file size (100MB limit)
            if (file.size > 100 * 1024 * 1024) {
                alert("File size should not exceed 100MB");
                return;
            }

            // Check file type
            if (!['application/pdf', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                alert("Only PDF, JPEG, and JPG files are allowed");
                return;
            }

            const documentName = getDocumentName();
            const category = getSelectedCategory();
            if (!documentName) return;

            const fileName = `${uuidv4()}_${file.name}`;
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${fileName}`);

            // Upload file to storage
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            const docData: any = {
                name: documentName,
                documentName: fileName,
                uploadTime: new Date(),
            };

            if (category) {
                docData.category = category;
            }

            // Add document record to Firestore
            await addDoc(collection(db, `companies/${companyId}/leads/${leadId}/documents`), docData);

            await fetchData(); // Refresh the documents list
            setSelectedDocument("");
            setSelectedCategory("");
            setEnteredDocumentName("");
            setToastMessage('Document uploaded successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Error uploading document. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error("Error uploading document:", error);
        } finally {
            setIsLoading(false);
            if (event.target) event.target.value = ''; // Reset file input
        }
    };

    const handleDocumentClick = async (document: Document) => {
        if (document.downloadUrl) {
            window.open(document.downloadUrl, '_blank');
        }
    };

    const handleUploadExisting = async (document: Document) => {
        if (!fileInputRef.current) return;
        fileInputRef.current.click();
        fileInputRef.current.onchange = async (event: Event) => {
            const target = event.target as HTMLInputElement;
            try {
                setIsLoading(true);
                const file = target.files?.[0];
                if (!file || !companyId || !leadId) return;

                // Check file size (100MB limit)
                if (file.size > 100 * 1024 * 1024) {
                    alert("File size should not exceed 100MB");
                    return;
                }

                // Check file type
                if (!['application/pdf', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                    alert("Only PDF, JPEG, and JPG files are allowed");
                    return;
                }

                const fileName = `${uuidv4()}_${file.name}`;
                const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${fileName}`);

                // Upload file to storage
                await uploadBytes(storageRef, file);
                const downloadUrl = await getDownloadURL(storageRef);

                // Update existing document
                const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
                await updateDoc(docRef, {
                    documentName: fileName,
                });

                await fetchData(); // Refresh the documents list
            } catch (error) {
                console.error("Error uploading document:", error);
            } finally {
                setIsLoading(false);
                if (target) target.value = ''; // Reset file input
            }
        };
    };

    const handleDelete = async (document: Document) => {
        try {
            setIsLoading(true);
            if (!companyId || !leadId) return;

            // If document was uploaded, delete from storage first
            if (document.documentName) {
                const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${document.documentName}`);
                await deleteObject(storageRef);
            }

            // Delete Firestore document
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
            await deleteDoc(docRef);

            await fetchData(); // Refresh the documents list
            setToastMessage('Document deleted successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Error deleting document. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error("Error deleting document:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatUploadTime = (date: Date) => {
        const timeString = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        const dateString = date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });

        return `${dateString}, ${timeString}`;
    };

    return (
        <div>
            <div className="mt-8 ml-12 w-3/4">
                <h2 className="font-montserrat font-medium text-2xl text-gray-900 mb-3">Document Management</h2>

                {/* Document Management Section */}
                <div className="mb-8">
                    <div className="flex items-center gap-4">
                        {/* Category Selection */}
                        <div className="flex-1">
                            <label htmlFor="select-category" className="block font-montserrat font-normal text-base text-black mb-2">
                                Select Category
                            </label>
                            <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="select-category"
                                        value={selectedCategory || "-- Select Category --"}
                                        onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                                        className={`w-full py-2 pl-4 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : ''}`}
                                        readOnly={!write}
                                        disabled={!write}
                                    />
                                    <button
                                        type="button"
                                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${!write ? 'cursor-not-allowed' : ''}`}
                                        onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                                        disabled={!write}
                                    >
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                            alt="Dropdown Arrow"
                                            className={`w-7 h-7 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>

                                    {isCategoryDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                            {documentCategories.length > 0 ? (
                                                documentCategories.map((category) => (
                                                    <button
                                                        key={category.id}
                                                        onClick={() => { 
                                                            setSelectedCategory(category.categoryName);
                                                            setSelectedDocument(""); // Reset document selection
                                                            setIsCategoryDropdownOpen(false); 
                                                        }}
                                                        className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100"
                                                    >
                                                        {category.categoryName}
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="px-4 py-2 text-gray-500">
                                                    No categories available
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Document Selection - Only show if category is selected */}
                        {selectedCategory && (
                            <div className="flex-1">
                                <label htmlFor="select-document" className="block font-montserrat font-normal text-base text-black mb-2">
                                    Select Document
                                </label>
                                <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="select-document"
                                            value={selectedDocument || "-- Select Document --"}
                                            onClick={() => setIsDocumentDropdownOpen(!isDocumentDropdownOpen)}
                                            className={`w-full py-2 pl-4 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : ''}`}
                                            readOnly={!write}
                                            disabled={!write}
                                        />
                                        <button
                                            type="button"
                                            className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${!write ? 'cursor-not-allowed' : ''}`}
                                            onClick={() => setIsDocumentDropdownOpen(!isDocumentDropdownOpen)}
                                            disabled={!write}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                alt="Dropdown Arrow"
                                                className={`w-7 h-7 transition-transform ${isDocumentDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>

                                        {isDocumentDropdownOpen && (
                                            <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-60 overflow-y-auto">
                                                {getAvailableDocuments().map((doc, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => { 
                                                            setSelectedDocument(doc); 
                                                            setIsDocumentDropdownOpen(false);
                                                            // Clear entered document name if user selects from dropdown
                                                            if (enteredDocumentName) {
                                                                setEnteredDocumentName("");
                                                            }
                                                        }}
                                                        className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100"
                                                    >
                                                        {doc}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-center my-4 relative">
                        <span className="bg-white px-3 font-montserrat font-medium text-xl text-gray-900">OR</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <label htmlFor="document-name" className="block font-montserrat font-normal text-base text-black mb-2">
                                Enter Document Name
                            </label>
                            <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                <input
                                    type="text"
                                    id="document-name"
                                    value={enteredDocumentName}
                                    onChange={(e) => {
                                        setEnteredDocumentName(e.target.value);
                                        // Clear selected document if user starts typing
                                        if (e.target.value && selectedDocument) {
                                            setSelectedDocument("");
                                            setSelectedCategory("");
                                        }
                                    }}
                                    className={`w-full py-2 pl-4 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write || !!selectedDocument ? 'cursor-not-allowed' : ''}`}
                                    readOnly={!write || !!selectedDocument}
                                    disabled={!write || !!selectedDocument}
                                    placeholder={selectedDocument ? "Document already selected above" : "Enter Document Name"}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-4 mt-6">
                        <button
                            onClick={handleRequestDocument}
                            disabled={isLoading || (!selectedDocument && !enteredDocumentName) || !write}
                            className={`text-white py-2 px-6 rounded-full ${isLoading || (!selectedDocument && !enteredDocumentName) || !write
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                                }`}
                            style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)' }}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Requesting...
                                </div>
                            ) : (
                                'Request Document'
                            )}
                        </button>

                        <div className="relative">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg"
                                onChange={handleFileUpload}
                                className={`hidden ${!write ? 'cursor-not-allowed' : ''}`}
                                readOnly={!write}
                                disabled={!write}
                            />
                            <button
                                onClick={handleUploadClick}
                                disabled={isLoading || (!selectedDocument && !enteredDocumentName) || !write}
                                className={`text-white py-2 px-6 rounded-full ${isLoading || (!selectedDocument && !enteredDocumentName) || !write ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Uploading...
                                    </div>
                                ) : (
                                    'Upload Document'
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Submitted Documents Section */}
                <div className="mt-12">
                    <h2 className="font-montserrat font-medium text-2xl text-gray-900 mb-3">
                        Submitted Documents
                    </h2>

                    <div className="space-y-8">
                        {(() => {
                            const { categorized, uncategorized } = groupDocumentsByCategory();
                            
                            return (
                                <>
                                    {/* Categorized Documents */}
                                    {Object.entries(categorized).map(([categoryName, categoryDocs]) => (
                                        <div key={categoryName} className="mb-8">
                                            <h3 className="font-montserrat font-medium text-lg text-gray-700 mb-4 border-b border-gray-200 pb-2">
                                                {categoryName}
                                            </h3>
                                            <div className="space-y-4">
                                                {categoryDocs.map((doc) => (
                                                    <div key={doc.id} className="relative">
                                                        <label className="block font-montserrat font-normal text-base text-black mb-2">
                                                            {doc.name}
                                                        </label>
                                                        <div
                                                            onClick={() => handleDocumentClick(doc)}
                                                            className={`relative w-full p-4 bg-white border border-gray-200 rounded-lg ${doc.downloadUrl || !write ? 'cursor-pointer hover:bg-gray-50' : ''
                                                                }`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/${doc.documentName ? (
                                                                            doc.documentName.toLowerCase().endsWith('.pdf')
                                                                                ? 'catppuccin.svg'
                                                                                : doc.documentName.toLowerCase().match(/\.(jpg|jpeg)$/)
                                                                                    ? 'teenyicons.svg'
                                                                                    : 'catppuccin.svg'
                                                                        ) : 'catppuccin.svg'
                                                                            }`}
                                                                        alt="Document Icon"
                                                                        className="w-6 h-6 mr-3"
                                                                    />
                                                                    <div>
                                                                        <p className="text-sm text-gray-600">
                                                                            {formatUploadTime(doc.uploadTime)}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500 mt-1">
                                                                            {doc.status === 'requested' ? 'Pending Upload' : (
                                                                                doc.documentName ? doc.documentName.split('_').slice(1).join('_') : 'Uploaded'
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {doc.status === 'requested' && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleUploadExisting(doc);
                                                                            }}
                                                                            className={`px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:opacity-90 ${!write ? 'cursor-not-allowed' : ''}`}
                                                                            disabled={isLoading || !write}
                                                                        >
                                                                            Upload
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDelete(doc);
                                                                        }}
                                                                        className={`px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#FFA782] to-[#FC583D] hover:opacity-90 ${!write ? 'cursor-not-allowed' : ''}`}
                                                                        disabled={isLoading || !write}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Uncategorized Documents */}
                                    {uncategorized.length > 0 && (
                                        <div className="mb-8">
                                            <h3 className="font-montserrat font-medium text-lg text-gray-700 mb-4 border-b border-gray-200 pb-2">
                                                Other Documents
                                            </h3>
                                            <div className="space-y-4">
                                                {uncategorized.map((doc) => (
                                                    <div key={doc.id} className="relative">
                                                        <label className="block font-montserrat font-normal text-base text-black mb-2">
                                                            {doc.name}
                                                        </label>
                                                        <div
                                                            onClick={() => handleDocumentClick(doc)}
                                                            className={`relative w-full p-4 bg-white border border-gray-200 rounded-lg ${doc.downloadUrl || !write ? 'cursor-pointer hover:bg-gray-50' : ''
                                                                }`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/${doc.documentName ? (
                                                                            doc.documentName.toLowerCase().endsWith('.pdf')
                                                                                ? 'catppuccin.svg'
                                                                                : doc.documentName.toLowerCase().match(/\.(jpg|jpeg)$/)
                                                                                    ? 'teenyicons.svg'
                                                                                    : 'catppuccin.svg'
                                                                        ) : 'catppuccin.svg'
                                                                            }`}
                                                                        alt="Document Icon"
                                                                        className="w-6 h-6 mr-3"
                                                                    />
                                                                    <div>
                                                                        <p className="text-sm text-gray-600">
                                                                            {formatUploadTime(doc.uploadTime)}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500 mt-1">
                                                                            {doc.status === 'requested' ? 'Pending Upload' : (
                                                                                doc.documentName ? doc.documentName.split('_').slice(1).join('_') : 'Uploaded'
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {doc.status === 'requested' && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleUploadExisting(doc);
                                                                            }}
                                                                            className={`px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:opacity-90 ${!write ? 'cursor-not-allowed' : ''}`}
                                                                            disabled={isLoading || !write}
                                                                        >
                                                                            Upload
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDelete(doc);
                                                                        }}
                                                                        className={`px-3 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#FFA782] to-[#FC583D] hover:opacity-90 ${!write ? 'cursor-not-allowed' : ''}`}
                                                                        disabled={isLoading || !write}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default RequestDocument;
